// Re-export ErrorDetail from shared package
export type { ErrorDetail, InputMode } from '@padcanvas/shared';
import type { QuestionFigure } from '@padcanvas/shared';
import type { ErrorDetail, InputMode } from '@padcanvas/shared';
export type { Solution } from '@shared-ui/questions-page';
import type { Solution } from '@shared-ui/questions-page';

// Helper to extract error message from ApiResponse.error
// Note: The shared package's getErrorMessage takes ApiResponse, this one takes just the error field
export function getErrorMessage(error: string | ErrorDetail | undefined): string {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  return error.detailString || error.detailWord || 'Unknown error';
}

export interface User {
  id: string;
  name: string;
  /** How the user signs in: an email address for anyone who registered with
   *  one, a provisioned handle (`1042.teoler`) for a roster account. */
  loginId?: string;
  email: string;
  notificationEmail?: string;  // optional override; notification system uses this when set
  phone?: string;  // E.164 — also the WhatsApp number for Stage-2 re-engagement
  whatsappReminders?: boolean;  // Stage-2 WhatsApp re-engagement opt-in (number is `phone`)
  /** T5: unified capability set — single source of truth. */
  roles?: import('../api/auth').UserRole[];
  grade: number;
  createdAt: string;
  /** Model calls made for this user (modelInputs), not submissions. */
  operationCount?: number;
  primaryTargetExam?: string;
  targetExams?: string[];
  subjects?: string[];  // User's selected subjects
  authProvider?: string;  // 'email' | 'google'
  ring?: string;  // 'devtesting' | 'beta' | 'prod' | 'guest'
  lastActivityAt?: string;
  /** Per-platform last app-open. Detail view only (the list endpoint omits it).
   *  See docs/FeatureDesigns/user-dyninfo.md. */
  lastOpenAt?: { ios?: string; android?: string; web?: string };
  lastAppVersion?: { ios?: string; android?: string };
  bypassSubscription?: boolean;
  pipelineConfig?: string;
  /** How this student's handwriting should be READ — transcription guidance
   *  handed to the extractor and the visual analyser. Admin-authored. */
  handwritingNotes?: string;
  deletedAt?: string;
  deletedBy?: string;
  invitedBy?: string;
  /** Display name resolved from `invitedBy`. Set by the admin list endpoint. */
  invitedByName?: string;
  subscriptions?: { subscriptionName: string; status: string; subjects: string[] }[];
  /** Seeded dummy user (populated social presence). Admin-ops only. */
  isDummy?: boolean;
  /** The section this student sits in — "10-B" — from their active class
   *  membership. Absent when they are in none. */
  section?: string;
  studentCount?: number;
  activeStudentCount7d?: number;
  teacherSubjects?: string[];
  teachers?: string[];
  tenantId?: string;
  /** Where this account came from — written once at creation, on every
   *  account (backend `TouchSchema`; published-links-target.md §7.1).
   *  Absent only on accounts that pre-date capture. */
  acquisition?: AcquisitionTouch;
  /** The product front door the account was signed up for. */
  signUpFor?: 'BuzzBrain' | 'ScienceXP';
}

export interface AcquisitionTouch {
  at: string;
  property?: 'student' | 'zitmus';
  landingPath?: string;
  referrerHost?: string;
  link?: { slug: string; publisher: { kind: 'partner'; id: string } | { kind: 'us' } | { kind: 'student'; userId: string }; arrivalId?: string };
  campaign?: string;
  variant?: string;
  /** `'provisioned'` on an account made by an admin or a teacher rather than
   *  arrived at; `provisionedAs` then says how. */
  source?: string;
  medium?: string;
  term?: string;
  /** Where a link was put — a closed vocabulary (yt-desc, ig-bio, wa, qr).
   *  Absent when the follower stated nothing. */
  placement?: string;
  /** seeded / admin-create / admin-invite / teacher-invite. */
  provisionedAs?: string;
  click?: { param: string; id: string };
}

export type ProficiencyLevel = 'low' | 'medium' | 'high';

// Question type — single source of truth in @padcanvas/shared. Re-exported
// here so existing admin-console code keeps its `import { QuestionType } from
// '../types'` paths working.
export type { QuestionType } from '@padcanvas/shared';
import type { QuestionType } from '@padcanvas/shared';

// MCQ option structure
export interface MCQOption {
  label: string;  // a, b, c, d
  text: string;
  imageUrl?: string;
  isCorrect?: boolean;
}

// Exam appearance - tracks which exams this question appeared in
export interface ExamAppearance {
  exam: string;       // JEE, NEET, CBSE, ICSE, STATE
  date?: string;      // Date of the exam (e.g., "2024-01-27")
  session?: string;   // Session (e.g., "Morning", "Afternoon", "Session 1")
  questionNo?: string; // Question number in the exam
  marks?: number;     // Marks for this question in this exam
}

// Related question types
export type RelationType = 'prerequisite' | 'follow-up' | 'similar-concept' | 'harder-variant' | 'easier-variant' | 'same-exam' | 'common-mistake-pair';

export interface RelatedQuestion {
  questionId: string;
  relationType: RelationType | string;
  reasonString: string;
  selectionCriteria?: Record<string, unknown>;
}

// Grade/Class levels
import type { GradeType, SubjectType, ProgrammeType } from '@padcanvas/shared';
export type Grade = GradeType;
export type { SubjectType };

export type ModuleLevel = 'module' | 'chapter' | 'topic';

// Module - first-class entity for tracking progress
/** Mirrors backend ModulePitfallSchema. A new one is sent without an id or
 *  audit fields — the server assigns them. */
export interface ModulePitfall {
  id?: string;
  pitfall: string;
  check: string;
  retired?: boolean;
  addedBy?: string;
  addedAt?: string;
  updatedAt?: string;
}

export interface Module {
  id: string;
  readableId: string;      // Single word, lowercase alphanumeric with underscores
  displayName: string;
  subject: SubjectType;
  // Per-board grade, grouped by grade: [grade, exams[]] — the module's ONLY grade
  // record. The board-blind `grades` array that used to sit here was a second copy of
  // the same fact and drifted from it; the API still returns it, derived, for clients
  // not yet migrated. See question-pools-and-entitlement/question-grade-board-model-vision.md.
  gradeBoards: [Grade, string[]][];
  /** @deprecated Derived from `gradeBoards` at the API boundary; never sent on save. */
  grades?: Grade[];
  description?: string;
  sortOrder: number;
  isActive: boolean;
  moduleLevel: ModuleLevel; // Hierarchy level: module → chapter → topic
  isTopModule: boolean;    // DEPRECATED: use moduleLevel. Kept for compat.
  parentModuleId?: string; // Reference to parent (chapter→module, topic→chapter)
  instructions?: Record<string, string>; // Named instructions for prompts (key → instruction text)
  /** Known traps of this topic, checked by the analyser only. Inherited by the
   *  modules below. See docs/FeatureDesigns/diagnosis/topic-pitfalls-target.md. */
  pitfalls?: ModulePitfall[];
  questionCount?: number;  // Number of questions in this module
  /** Teacher-authored questions in this module, counted separately from the
   *  library: an admin sees every teacher's, a teacher only their own. Both
   *  counts are populated only when the caller asks for `withCounts`. */
  teacherQuestionCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

// ── Book registry (see docs/FeatureDesigns/question-pools-and-entitlement/question-source-type-target.md) ──
// Mirrors backend PublisherSchema/BookSchema — the normalized source for
// textbook-origin questions.
export const PUBLISHER_OPTIONS = [
  'NCERT', 'Selina', 'Allen', 'Aakash', 'Resonance', 'FIITJEE', 'Vedantu',
  'Cengage', 'Arihant', 'MTG', 'Errorless', 'Disha', 'Bharati Bhawan',
  'Narayana', 'Bansal', 'Other',
] as const;
export type Publisher = typeof PUBLISHER_OPTIONS[number];

export interface Book {
  id: string;
  title: string;
  author?: string;         // book author, e.g. "HC Verma"
  publisher?: Publisher;   // publishing house / institute
  subject?: SubjectType;
  board?: ProgrammeType;  // board / exam track (CBSE, ICSE, JEE…)
  grades?: Grade[];        // class(es) the book targets
  edition?: string;
  displayString?: string;
  isActive?: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
}

// Question origin axis — mirror of the backend QuestionSourceType.
export type QuestionSourceType =
  | 'previous-year-paper' | 'textbook' | 'ai-generated' | 'online' | 'manual' | 'unknown';

// Embedded on a question — reference to a Book + where in it.
export interface BookRef {
  bookId: string;
  bookName: string;        // denormalized Book.title
  location?: string;
}

// ── Curriculum model (see docs/FeatureDesigns/modules-curriculum-topics-concepts/concept-curriculum-model-target.md) ──
export type PlacementNodeKind = 'module' | 'chapter' | 'topic' | 'concept';
export type BookLevel = 'unit' | 'chapter' | 'section' | 'subsection';

// A Program is one entitlement rule: IF the student's profile matches `applies`,
// THEN they receive `grants` (board-grade pools). Resolution is match-all-and-
// union. See docs/FeatureDesigns/question-pools-and-entitlement/question-pools-and-programs-target.md §3 L4.
export interface ProgramGrant {
  board: ProgrammeType;      // pool board or exam track
  grade?: Grade;              // POOL's grade — omitted only for gradeless exam tracks
}
export interface Program {
  id: string;                 // stable key, e.g. 'jee-foundation-9'
  name: string;               // 'JEE Foundation — Class 9'
  applies?: { grade?: Grade; targetExam?: ProgrammeType }; // STUDENT profile match; empty = never auto-matches
  grants: ProgramGrant[];
  description?: string;
  isActive?: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
}
export interface ProgramListItem extends Program {
  lastModified?: number;      // Cosmos _ts (epoch seconds)
}

// A Curriculum is one syllabus = (board/exam, subject, grade), e.g. "CBSE Class 8 Maths".
export interface Curriculum {
  id: string;            // stable key, e.g. 'cbse-8-maths'
  name: string;          // 'CBSE Class 8 Maths'
  board: ProgrammeType; // board (CBSE/ICSE) or exam track (JEE/NEET)
  subject: SubjectType;  // syllabus subject
  grade?: Grade;         // syllabus grade (omitted for exam tracks)
  // The pool this curriculum's questions live in. Derived from board + grade
  // when that identifies it ('cbse:10'); stored only when minted, which a
  // programme with no grades needs — every AP course would otherwise share the
  // bare key 'ap'. See docs/FeatureDesigns/no-content-curricula-target.md.
  poolKey?: string;
  examTrack?: boolean;   // true = exam track (JEE/NEET) vs school board
  // Ownership: absent/'admin' = platform-authored and public; 'teacher' = a
  // teacher's private copy, which must NOT be picked up as a class's syllabus.
  ownerType?: 'admin' | 'teacher';
  teacherId?: string;
  description?: string;
  isActive?: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
}

// List-row augmentation returned by listCurricula (board-book-level counts + _ts).
export interface CurriculumListItem extends Curriculum {
  counts: { unit: number; chapter: number; section: number; subsection: number };
  lastModified?: number; // Cosmos _ts (epoch seconds)
  // Lost-question coverage stat, computed on last curriculum open. Absent until first opened.
  coverage?: { lostPct: number; lostCount: number; total: number; computedAt: string };
  // Chapters below the practisable-size threshold. Absent until first opened.
  thinChapters?: { count: number; total: number; threshold: number };
}

// A book-ToC entry (chapter/section) that optionally maps to a canonical node.
export interface CurriculumPlacement {
  id: string;
  curriculumId: string;
  parentId?: string;            // FK → parent placement (chapter for a topic)
  bookLevel: BookLevel;         // this entry's level in the book
  number: string;               // printed label, e.g. '1' or '1.1'
  displayName: string;          // book's title (verbatim)
  order?: number;
  canonicalNodeId?: string;     // OPTIONAL primary canonical mapping; empty = unmapped header
  canonicalNodeIds?: string[];  // additional canonical modules this book node maps to (many-to-one)
  /** Effort inputs: pages from the book's index, density authored per chapter.
   *  Hours are derived server-side at read time and never stored. */
  pageCount?: number;
  complexityFactor?: 'very-light' | 'light' | 'medium' | 'dense' | 'very-dense';
  nodeKind?: PlacementNodeKind;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
}

// Concept registry (Phase 2; see concept-curriculum-model.md §4). The gateable
// registry is the filtered view (gateable=true). Grade is NOT stored here.
export interface Concept {
  id: string;                    // stable id, e.g. 'dot-product'
  subject: SubjectType;          // partition key
  name: string;
  aliases: string[];             // alt names; power alias auto-suggest
  introducedAtTopicId?: string;  // FK → modules leaf that introduces it (soft ref)
  gateable: boolean;             // true = checked before a hint uses it
  description?: string;
  // Concept hierarchy (see problem-type-similarity.md §4)
  parentConceptId?: string;         // containment parent (FK → concepts.id); unit nodes have none
  level?: 'unit' | 'group' | 'concept';  // granularity level in the containment tree
  prerequisiteConceptIds?: string[];// directed knowledge-dependency edges
  relatedConceptIds?: string[];     // undirected adjacency edges
  grades?: number[];                // class numbers where in scope, e.g. [11]
  examScope?: string[];             // exam tracks, e.g. ['JEE-Main','JEE-Advanced']
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
}

/** Problem type — equivalence class of questions anchored to a granular concept. */
export interface ProblemType {
  id: string;
  primaryConceptId?: string;    // the fine concept it anchors to (containment parent)
  difficultyBand?: string;
  label?: string;
  size: number;
  representativeQuestionId?: string;
  reviewStatus?: string;
}

// A typed edge in the type-level navigation graph (problem-type-relations).
export interface ProblemTypeRelation {
  id: string;
  fromTypeId: string;
  toTypeId: string;
  relationType: string;         // harder/easier-variant, prerequisite, follow-up, similar-concept
  direction?: string;           // 'from-to' (directed) | 'none' (undirected)
  confidence?: number;
  reason?: string;
  bandDelta?: number;
  reviewStatus?: string;
}

// One node for the graph traverser: a type, its member questions, its outgoing edges.
export interface ProblemTypeGraphMember {
  id: string;
  questionText: string;
  difficulty?: string;
  isRepresentative: boolean;
}
export interface ProblemTypeGraphEdge {
  id: string;
  relationType: string;
  direction?: string;
  confidence?: number;
  reason?: string;
  bandDelta?: number;
  reviewStatus?: string;
  toType: { id: string; label?: string; difficultyBand?: string; size: number } | null;
}
export interface ProblemTypeNode {
  type: { id: string; label?: string; difficultyBand?: string; primaryConceptId?: string; size: number; representativeQuestionId?: string; reviewStatus?: string };
  members: ProblemTypeGraphMember[];
  edgesOut: ProblemTypeGraphEdge[];
}

/** Health of the concept/problem-type substrate for one subject.
 *  What each field means: docs/FeatureDesigns/modules-curriculum-topics-concepts/concept-graph-current.md §3. */
export interface ConceptGraphSubjectStats {
  subject: string;
  concepts: {
    total: number;
    byLevel: Record<string, number>;
    fine: number;
    anchored: number;
    orphans: number;
    withPrerequisites: number;
  };
  problemTypes: {
    total: number;
    singletons: number;
    conceptBandGroups: number;
    typesPerGroup: number;
    bands: Record<string, number>;
    largestAnchor: number;
    anchorsOverTwenty: number;
    byReviewStatus: Record<string, number>;
  };
  relations: {
    total: number;
    byType: Record<string, number>;
    byReviewStatus: Record<string, number>;
    unreviewedDirected: number;
  };
  questions: {
    jeeTotal: number;
    tagged: number;
    typed: number;
    withRelated: number;
    coveragePct: number;
    compression: number;
    byDifficulty: Record<string, number>;
  };
  embeddings: number;
  unservablePrerequisites: { links: number; broken: number };
}

export interface ConceptGraphStats {
  generatedAt: string;
  subjects: ConceptGraphSubjectStats[];
}

export interface CurriculumTocNode {
  id: string;
  parentId?: string;
  bookLevel: string;
  number: string;
  displayName: string;       // book title
  order?: number;
  grade?: string;
  canonicalNodeId?: string;  // optional mapping
  canonicalName?: string;
  canonicalLevel?: string;
  canonicalPath?: string;
}

// Module import result types
export interface ModuleFieldChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface ModuleImportStatus {
  readableId: string;
  displayName: string;
  status: 'created' | 'updated' | 'unchanged' | 'error';
  id?: string;
  changes?: ModuleFieldChange[];
  error?: string;
}

export interface ModuleImportResult {
  created: number;
  updated: number;
  unchanged: number;
  errors: number;
  modules: ModuleImportStatus[];
}

export interface SubjectProficiencyInfo {
  level: ProficiencyLevel;
  isManualOverride: boolean;
  questionsAttempted: number;
  averageScore: number;
}

export interface Question {
  id: string;
  subject: string; // 'physics' | 'maths' | 'chemistry' | 'biology'
  topic: string;
  module?: { id: string; name: string } | null;  // Canonical module reference (single source of truth)
  difficulty?: 'easy' | 'medium' | 'hard';
  difficultyLevel?: 'easy' | 'medium' | 'hard';
  /** Pool membership — which (board, grade) pools this question serves. */
  boardGrades?: string[];
  /** Derived from `boardGrades` at the write funnel; not authored. */
  class?: string[];
  questionText: string;
  /** The number as printed on the source paper ('01.', 'Q7', '2(a)'); absent on hand-authored questions. */
  sourceQuestionNumber?: string;
  questionDiagramUrl?: string | null;
  figures?: QuestionFigure[] | null;
  questionDiagramHash?: string;
  solutionImageHash?: string;
  /** Where each image was cut from on the source paper.
   *  See docs/FeatureDesigns/question-image-recrop-target.md. */
  sourceRegions?: import('@padcanvas/shared').QuestionSourceRegion[] | null;
  correctAnswer: string;
  solutions?: Solution[];    // Multiple solutions across (style, targetExams, label) dimensions
  hints?: string[];
  /**
   * Per-question cap on hints the student can reveal.
   * Covers both the pre-written `hints` above AND hints generated at runtime by the LLM.
   * Overrides `AppSettings.maxHintsForStudent`.
   */
  maxHintsForStudent?: number;
  targetExams?: string[];  // ['JEE', 'NEET', etc.] - exams this question is relevant for
  usesConcepts?: string[];  // Concept ids this question uses (Phase 2 tagging)
  primaryConceptId?: string; // The question's anchor fine concept (FK → concepts.id)
  problemTypeId?: string;   // The problem type this question belongs to (FK → problem-types.id)
  avoidConcepts?: string[]; // Concept ids to always avoid in hints — L3 hard override
  // MCQ fields
  questionType?: QuestionType;
  errorMargin?: number;
  options?: MCQOption[];
  solutionImageUrl?: string | null;
  // Debug data from LLM extraction (bounding boxes for diagrams/regions)
  debug?: {
    llmProvider: string;
    llmModel: string;
    pageDimensions: { pageNumber: number; width: number; height: number }[];
    pageDimensionForModel?: { pageNumber: number; width: number; height: number }[];
    fullQuestionBoundingBox: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    questionTextBoundingBoxes: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    questionNumberLabelBoundingBox: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    questionDiagramBoundingBoxes: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    solutionDiagramBoundingBoxes: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    optionsDiagramBoundingBoxes: { pageNumber: number; label: string; x: number; y: number; width: number; height: number }[];
    // Adjusted bounding boxes (scaled from LLM model coords to actual image coords, used for cropping)
    adjustedFullQuestionBoundingBox?: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    adjustedQuestionTextBoundingBoxes?: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    adjustedQuestionNumberLabelBoundingBox?: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    // Full question bounding box with dimensions scaled to 0-1 range (whole page = 1x1)
    fullQuestionBoundingBox0to1?: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    adjustedQuestionDiagramBoundingBoxes?: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    adjustedSolutionDiagramBoundingBoxes?: { pageNumber: number; x: number; y: number; width: number; height: number }[];
    adjustedOptionsDiagramBoundingBoxes?: { pageNumber: number; label: string; x: number; y: number; width: number; height: number }[];
  };
  // Exam appearance history
  appearedInExams?: ExamAppearance[];
  // Question readiness/version - tracks enhancement iterations
  questionReadiness?: number;
  // Enrichment pipeline status: 0=not needed, 1=pending, 2=basic enriched, 3=fully enriched
  enrichStatus?: number;
  enrichmentNotes?: string;
  // Paper import tracking
  importStatus?: 'draft' | 'live';
  sourceImportId?: string;
  importBatchId?: string;
  questionSource?: string;
  // Question origin (see docs/FeatureDesigns/question-pools-and-entitlement/question-source-type-target.md)
  sourceType?: QuestionSourceType;
  bookSources?: BookRef[];   // textbook origin — refs into the book registry
  sourceUrl?: string;        // online origin — where on the web
  relatedQuestions?: RelatedQuestion[];
  /** Custody: whose question this is. Absent ⇒ a library row, owned by nobody.
   *  See question-pools-and-entitlement/question-ownership-target.md. */
  ownerId?: string;
  ownerType?: 'teacher' | 'student';
  /** @deprecated No longer written or projected (Track B, B4). Older responses
   *  may still carry it; read `ownerId`. */
  teacherId?: string;
  // Default marks for this question (single-part only; STRUCTURED derives from parts)
  marks?: number;
  // Searchable keywords
  keywords?: string[];
  // Lettered sub-parts under one question number; present when questionType === 'STRUCTURED'.
  // See docs/FeatureDesigns/question-structure.md.
  parts?: QuestionPart[];
  // STRUCTURED choice / K-of-N expression (Phase 1C). Absent ⇒ all parts required.
  requirement?: PartRequirement;
  // Reviewer/SME annotations on this question.
  comments?: QuestionComment[];
  // CosmosDB system timestamp (epoch seconds)
  _ts?: number;
}

// A reviewer/SME annotation on a question (mirrors the backend QuestionComment).
export interface QuestionComment {
  commentBy: string;
  commentByType: 'student' | 'teacher' | 'admin';
  commentOn: string;        // ISO timestamp
  commentText: string;
  commentType?: string;
}

// QuestionPart lives in @padcanvas/shared (single source of truth — backend
// validates via zod, frontend forms bind directly to this type).
// Re-exported here so existing `import type { ..., QuestionPart } from '../types'`
// patterns inside admin-console keep working.
export type { QuestionPart, PartRequirement } from '@padcanvas/shared';
import type { QuestionPart, PartRequirement } from '@padcanvas/shared';

// Lightweight question list item (from GET /question-list)
export interface QuestionListItem {
  id: string;
  questionText: string;
  difficulty: string;
  difficultyLevel?: string;
  questionType: string | null;
  module: { id: string; name: string } | null;
  appearedInExams: ExamAppearance[] | null;
  targetExams: string[] | null;
  importStatus?: string | null;
  questionReadiness?: number | null;
}

export interface QuestionListResponse {
  questions: QuestionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

// Feedback types — canonical definitions from @padcanvas/shared
import type { Feedback, LlmValueCorrectness, LlmUnitCorrectness, OverallAssessment } from '@padcanvas/shared';
export type {
  Feedback,
  StepAnalysis,
  OverallAssessment,
  LlmCorrectness,
  LlmValueCorrectness,
  LlmUnitCorrectness,
  Speed,
  SpeedCategory,
  ComputedScores,
  Approach,
  CorrectAnswerApproach,
  WrongAnswerApproach,
} from '@padcanvas/shared';

export interface ClientInfo {
  platform: 'ios' | 'android' | 'web';
  appVersion?: string;
  deviceModel?: string;
  osVersion?: string;
  locale?: string;
  screenSize?: string;
}

// Single feedback entry on an assessment
export interface FeedbackEntry {
  rating: 'positive' | 'negative';
  comment?: string;
  submittedAt: string;
  userId: string;
  role: 'student' | 'teacher' | 'admin';
}

// Assessment feedback — array of entries from different users
export type UserFeedback = FeedbackEntry[];

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

/** Mirrors the backend's AnswerReadSummary (answerRead/types.ts). */
export interface AnswerSecondRead {
  found: boolean;
  finalAnswer: string;
  option?: string;
  optionSource?: 'written_label' | 'value' | 'model_match';
  crossedOut: boolean;
  legibility: number;
  correct: 'yes' | 'no' | 'unknown';
  servedAnswer?: string;
  servedOption?: string;
  agreesWithServed: 'yes' | 'no' | 'unknown';
  note?: string;
  modelInputId?: string;
  readAt: string;
  trigger: { kind: 'check'; checks: string[] } | { kind: 'low_confidence'; confidence: number } | { kind: 'sample' } | { kind: 'admin' };
}

export interface AnswerSecondReadResult {
  status: 'read' | 'skipped';
  reason?: string;
  summary?: AnswerSecondRead;
}

export interface Submission {
  id: string;
  userId: string;
  questionId: string;
  /** The attempt this answer belongs to, when it came from one. What lets this
   *  page correct the region a question was read from — a correction is a fact
   *  about an attempt, not about a submission.
   *  See docs/FeatureDesigns/assignments-tests/solution-region-correction-target.md. */
  testSessionId?: string;
  canvasImageUrl?: string;
  /** Bytes of the canvas/photo blob, captured at upload time. */
  canvasImageSizeBytes?: number;
  typedText?: string;
  selectedOption?: string;  // For MCQ questions - the selected option label (a, b, c, d)
  prompt?: string;
  rawResponse?: string;
  feedback?: Feedback;
  /** How the page was read, every claim, and how far each can be trusted.
   *  See diagnosis/accuracy/diagnosis-selection-target.md. */
  evidence?: import('../pages/operations/EvidencePanel').EvidenceView;
  /** Scores denormalized onto the submission at analyze time (re-copied on
   *  re-evaluation). `finalScore` is what list views render — reading it avoids
   *  joining modelOutputs just for a number. Absent on pre-cutover rows. */
  /** Derived per-attempt signals — where the solution broke, and how the
   *  student started. Written beside `computedScores` at analyse time; absent
   *  on rows predating them, which reads as unknown rather than as zero. */
  attemptSignals?: {
    firstWrongStep?: number;
    stepCount?: number;
    method?: 'derive' | 'substitute' | 'test-options' | 'recall-formula' | 'eliminate' | 'mixed' | 'unclear' | 'none';
    diagnosisCategory?: string;
    /** Either vocabulary (DiagnosisKind in @padcanvas/shared). */
    diagnosisKind?: string;
    /** Vocabulary 2 (diagnosis-structure-target §5); absent version = 1. */
    diagnosisStage?: string;
    diagnosisCertainty?: 'clear' | 'tentative';
    diagnosisVocabularyVersion?: number;
  };
  computedScores?: {
    correctnessScore?: number;
    correctnessScoreComment?: string;
    approachScore?: number;
    approachScoreComment?: string;
    speedScore?: number;
    speedScoreComment?: string;
    finalScore?: number;
  };
  /** Summed estimated USD across every LLM run behind this analysis, written at
   *  analyze time. Denormalized for the same reason as `computedScores`. Absent
   *  on pre-cutover rows and on failures. */
  totalCostUsd?: number;
  /** Escalated-away runs — re-fired at a stronger tier after a quality check
   *  rejected them. Stamped with cost; absent on older rows. */
  escalationCount?: number;
  timeTakenSeconds?: number;  // Time taken by student to solve (from client)
  submittedAt: string;
  /** The question as it stands NOW. Detail views show `questionAsAttempted` instead when
   *  it exists — the score was produced against that, not against this. */
  question?: Question;
  /** The question the grader saw (ModelInput snapshot). Absent for skipped submissions
   *  and rows written before the capture existed. */
  questionAsAttempted?: {
    id?: string;
    questionText?: string;
    questionDiagramUrl?: string;
    figures?: QuestionFigure[] | null;
    correctAnswer?: string;
    questionType?: string;
    options?: { label?: string; text?: string; isCorrect?: boolean }[];
    /** STRUCTURED sub-parts as the grader saw them. */
    parts?: QuestionPart[];
  } | null;
  /** Meaningful fields edited since the attempt. Empty when nothing drifted or there is
   *  no snapshot to compare. */
  questionDriftFields?: ('questionText' | 'correctAnswer' | 'options' | 'questionDiagramUrl')[];
  /** Every learning moment the detector fired on THIS submission, applied or
   *  missed. Shown beside the parsed diagnosis: the diagnosis is the finding and
   *  the moments are what we did with it, and either alone leaves a reader
   *  unable to tell a bad diagnosis from a detector that ignored a good one. */
  learningMoments?: {
    learningMomentId: string;
    name: string;
    applied: boolean;
    context?: string;
    detectedAt?: string;
    /** Set when the analysis this fired on was withdrawn — shown as retracted
     *  rather than dropped, so the evidence the mark preserves stays visible. */
    withdrawn?: {
      reason: 'disputed' | 'superseded' | 'discarded' | 'rerun' | 'rejected';
      at: string;
      /** `rejected` only — who, by id and by name, and why. */
      by?: string;
      byName?: string;
      comment?: string;
    };
  }[];
  user?: Pick<User, 'id' | 'name' | 'email' | 'grade' | 'pipelineConfig'> & { subjectProficiency?: SubjectProficiencyInfo; ring?: string };
  // Model run fields
  primaryPromptId?: string;
  primaryProvider?: string;
  totalTokens?: number;
  totalEstimatedCostUsd?: number;
  runCount?: number;
  clientInfo?: ClientInfo;
  // User feedback on analysis quality
  userFeedback?: UserFeedback;
  // Target exam resolved for this submission (drives prompt selection)
  targetExamForSubmission?: string;
  /** Independent answer-only reading of the uploaded page, when one ran.
   *  See docs/FeatureDesigns/solution-analysis/answer-second-read-target.md. */
  answerSecondRead?: AnswerSecondRead;
  // Rerun tracking
  rerunFromSubmissionId?: string;
  // Input mode
  inputMode?: InputMode;
  // Model input reference (for auto-import to eval datasets)
  modelInputId?: string;
  /** Lifecycle status. Set on submissions pre-created at analyze enqueue time
   *  (post-cutover); absent on legacy rows. See
   *  docs/FeatureDesigns/pending-followups.md §1. */
  status?: 'analyzing' | 'analyzed' | 'failed';
}

export interface ManualEvaluation {
  accuracy: number;        // 1-5: How accurate is the LLM's score/assessment?
  feedbackQuality: number; // 1-5: How helpful and clear is the feedback?
  completeness: number;    // 1-5: Does it identify all issues/correct steps?
  notes?: string;          // Optional notes from evaluator
  evaluatedAt: string;
  evaluatedBy?: string;
}

/** Parsed `extract_steps` output — captured on the run for display in
 *  the admin Submission Detail's Parsed Feedback panel. The backend writes
 *  this on the ModelRun for any extract_steps run (see buildModelRuns.ts). */
export interface ExtractedStep {
  stepNumber: number;
  content: string;
  confidence: number;
  crossedOut: boolean;
}
export interface ExtractedStepsData {
  workQuality?: string;
  workQualityConfidence?: number;
  steps: ExtractedStep[];
  studentAnswer?: string;
}

// Model runs for a submission are represented as `ModelOutput[]` — see the
// canonical shape declared further down (also used by Hint / Concept Lab
// detail pages via `ModelInputWithOutputs`). The legacy `ModelRunRecord`
// and `Experiment` types were removed during convergence — admin client
// reads the persistence shape directly.

export interface HintRequest {
  id: string;
  userId: string;
  questionId: string;
  hint: string;
  category?: string;
  level?: string;
  detectedState?: string;
  difficulty?: string;
  shouldAllowNextHint?: boolean;
  llmProvider: string;
  latencyMs: number;
  createdAt: string;
  updatedAt?: string;
  inputMode?: InputMode;
  /** Resolved pipeline config that ran (override → else default). Undefined on legacy rows. */
  pipelineConfig?: string;
  hintHistory?: Array<Record<string, unknown>>;
  /** Absolute blob URL of the student's canvas/photo, resolved server-side
   *  from the linked ModelInput. Drop straight into an <img src>. */
  canvasImageUrl?: string;
  /** Bytes of the canvas/photo blob, captured at upload time. */
  canvasImageSizeBytes?: number;
  /** Typed-text input mirrored from the linked ModelInput. */
  typedText?: string;
  // Links to ModelInput/ModelOutput
  modelInputId?: string;
  modelOutputId?: string;
  // Joined data from backend
  question?: Question | null;
  user?: Pick<User, 'id' | 'name' | 'email' | 'grade'> & { pipelineConfig?: string } | null;
  /** Lifecycle of the most recent hint turn. See
   *  docs/FeatureDesigns/pending-followups.md §1. Absent on pre-cutover docs. */
  status?: 'generating' | 'completed' | 'failed';
  /** All ModelInputs in this hint session, ordered by hint turn (oldest first).
   *  Each entry carries its outputs and a 1-based `turnIndex` that aligns with
   *  `hintHistory[turnIndex - 1]`. `studentSolution.imageUrl` is the resolved
   *  absolute blob URL for that turn's canvas, ready for an <img src>. */
  inputsByTurn?: HintTurnInputWithOutputs[];
}

/** ModelInput plus its ModelOutputs plus the 1-based hint-turn index it belongs
 *  to. Used in the hint detail view to show every turn's prompt runs and the
 *  canvas the student had drawn at that turn. */
export interface HintTurnInputWithOutputs extends ModelInput {
  turnIndex: number;
  outputs: ModelOutput[];
}

/** One detect-learning-moments run (Operations → Learning Moments). */
export interface LearningMomentRun {
  id: string;
  generatedAt: string;
  latencyMs?: number;
  cost?: number;
  submissionId?: string;
  question?: { id: string; text: string; subject: string; topic: string } | null;
  /** A whole-sitting run: one call over these question numbers of the sitting. */
  sitting?: { sessionId: string; numbers: number[]; of: number } | null;
  user?: { id: string; name: string; email: string; grade: number } | null;
  moments: Array<{
    learningMomentId: string;
    name: string;
    wasPositive: boolean;
    context: string;
    /** Sitting runs only: which question the moment was reported on. */
    q?: number;
  }>;
}

export interface ConceptLabEntry {
  id: string;         // submissionId
  userId: string;
  questionId: string;
  conceptLab: {
    /** Lifecycle of the generation. Absent on pre-cutover entries — treat as completed. */
    status?: 'generating' | 'completed' | 'failed';
    generatedAt: string;
    promptId?: string;
    provider?: string;
    latencyMs?: number;
    modelInputId?: string;
    response?: {
      conceptSpotlight: string;
      personalizedNarrative: string;
      probingQuestions: Array<{
        id: string;
        question: string;
        options: string[];
        correctOptionIndex: number;
        explanation: string;
      }>;
      mistakeTargetedQuestions: Array<{
        source: 'related' | 'generated';
        questionId: string | null;
        questionText: string;
        relevanceReason: string;
      }>;
      relatedConcepts: Array<{
        conceptName: string;
        relationship: string;
        briefDescription: string;
      }>;
    };
    interactions: Array<{
      probingQuestionId: string;
      studentAnswer: number;
      isCorrect: boolean;
      timeToAnswerMs: number;
      answeredAt: string;
    }>;
    /** Resolved pipeline config that ran (override → else default). Undefined on legacy rows. */
    pipelineConfig?: string;
  };
  question?: Question | null;
  user?: Pick<User, 'id' | 'name' | 'email' | 'grade'> & { pipelineConfig?: string } | null;
  /** Student work resolved server-side from the concept lab's ModelInput. */
  canvasImageUrl?: string;
  canvasImageSizeBytes?: number;
  typedText?: string;
  inputMode?: InputMode;
}

export interface AdminStats {
  totalUsers: number;
  totalSubmissions: number;
  totalQuestions: number;
  submissionsByProvider: { provider: string; count: number }[];
  averageScore: number;
  recentActivity: {
    date: string;
    submissions: number;
  }[];
  dailyTrend: {
    date: string;
    submissions: number;
    llmCalls: number;
    estimatedCostUsd: number;
  }[];
  dailyProviderBreakdown: {
    date: string;
    provider: string;
    llmCalls: number;
    estimatedCostUsd: number;
  }[];
  dailyUsers: {
    date: string;
    activeUsers: number;
    newUsers: number;
  }[];
  activityEngagement: ActivityEngagementSeries[];
}

/** One student's row in the Solve Sources table. `counts` is keyed by the
 *  column keys the server sends, which differ per axis. */
export interface QuestionSourceRow {
  userId: string;
  name: string;
  email?: string;
  counts: Record<string, { questions: number; attempts: number }>;
  total: { questions: number; attempts: number };
}

export interface QuestionSourcesResponse {
  from: string;
  to: string;
  days: number;
  axis: 'source' | 'grant';
  /** Server-owned, so a new session purpose becomes a column with no UI edit. */
  columns: { key: string; label: string }[];
  rows: QuestionSourceRow[];
  total: number;
  page: number;
  pageSize: number;
  unresolvedSessions: number;
}

// Engagement chart vocabulary — single source of truth in @padcanvas/shared,
// alongside the backends that produce these series. Re-exported so callers keep
// importing it from here.
export type { EngagementActivityKind, ActivityEngagementSeries } from '@padcanvas/shared';
import type { ActivityEngagementSeries } from '@padcanvas/shared';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string | ErrorDetail;
  requestId?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ============================================
// Model Input/Output Types (Eval System)
// ============================================

export interface ModelInputQuestion {
  id: string;
  text: string;
  subject: string;
  topic: string;
  difficulty: string;
  questionType: QuestionType;
  correctAnswer?: string;
  solutions?: Solution[];
  imageUrl?: string;
  /** STRUCTURED questions: per-part snapshot (statements, marks, diagramUrl). */
  parts?: QuestionPart[];
}

export interface ModelInputStudentProfile {
  class: string;
  proficiency: 'low' | 'medium' | 'high';
  targetExam?: string;
}

export interface ModelInputStudentSolution {
  /** The pages JOINED into one image. Kept for anything that wants a single
   *  picture; it is NOT what the readers were given when `pageImageUrls` is
   *  present. */
  imageUrl?: string;
  imageSizeBytes?: number;
  /** Mode 3 multi-page: the pages the readers actually received, in order.
   *  Shown in preference to the composite — a run page that displays a join the
   *  model never saw invites the wrong diagnosis. */
  pageImageUrls?: string[];
  textAnswer?: string;
  selectedOption?: string;
  timeTakenSeconds?: number;
}

export interface ModelInput {
  id: string;
  partitionKey: string;
  source: 'production' | 'eval';
  userId?: string;
  submissionId?: string;
  datasetId?: string;
  /** Absent on authoring operations (extraction, enrichment, generation):
   *  they have no student and no source question, so these blocks would be
   *  placeholders and invented defaults. The real inputs are in
   *  `operationContext`. */
  question?: ModelInputQuestion;
  studentProfile?: ModelInputStudentProfile;
  studentSolution?: ModelInputStudentSolution;
  /** Which operation produced this input ('analysis', 'extract_verbatim', …). */
  operation?: string;
  /** Inputs that don't fit the analysis shape. Operations with no student
   *  solution put theirs here — e.g. extraction records the worksheet page
   *  images it attached, which ARE the model's input but are not a student's
   *  answer canvas. */
  operationContext?: {
    sourceKind?: string;
    sourceFileName?: string;
    /** The exact images sent to the model, post-resize. */
    attachedImages?: { url: string; label: string }[];
    /** What this particular call covered — 'pages 1–4', 'Q17–Q28'. Declared
     *  rather than left to the index signature below, which types it `unknown`
     *  and makes it unrenderable. `ModelOutput.runInfo` is joined from here. */
    runInfo?: string;
    [key: string]: unknown;
  };
  createdAt: string;
  /** A placeholder written when the input's first call started; the operation
   *  fills it in when it saves its input (model-output-lifecycle-target.md). */
  pending?: true;
}

export interface ModelOutputParameters {
  promptId: string;
  promptVersion: number;
  /** `YYYY-MM-DD.N` label for the prompt TEXT this run used. `promptVersion`
   *  is an environment-local counter — dev and prod number the same text
   *  differently — so this is the field to compare across environments.
   *  Absent on rows written before the label existed. */
  promptRev?: string;
  provider: string;
  model: string;
  /** Legacy temperature field — kept for backward compat. Prefer `actual.temperature`. */
  temperature: number;
  /** Actual parameters the adapter sent to the SDK. Absent on legacy rows. */
  actual?: ActualLLMParameters;
  /** Cross-provider failover: the originally-requested provider, present only
   *  when a backup provider actually served the call. */
  requestedProvider?: string;
  /** Providers tried and failed before the served one (failover chain).
   *  Each failed attempt ALSO has its own ModelOutput row
   *  (`status: 'failover_attempt'`) carrying its raw response, tokens and cost;
   *  this stays a lightweight breadcrumb so the runs table can render the
   *  failover badge without a join. */
  failedProviders?: { key: string; error: string; code?: string; providerStopReason?: string; latencyMs?: number }[];
}

/** Mirror of backend `ActualLLMParameters` — common fields + family-specific subblock. */
export interface ActualLLMParameters {
  temperature: number | null;
  maxTokens?: number;
  hasSystemPrompt?: boolean;
  outputFormat?: 'json_schema' | 'json_object' | 'text';
  family: ActualFamilyParams;
  tierRouting?: TierRoutingTrace;
  parallelRouting?: ParallelRoutingTrace;
}

// Quality-check + tier-routing trace — single source of truth in
// @padcanvas/shared (persisted wire types; no more hand-rolled mirror).
export type { QualityCheckName, TierRoutingTrace } from '@padcanvas/shared';
import type { QualityCheckName, TierRoutingTrace } from '@padcanvas/shared';

/** Mirror of backend `NormalizedStopReason` (modelrun/llmCall/base.ts). */
export type NormalizedStopReason =
  | 'stop' | 'max_tokens' | 'safety' | 'refusal' | 'tool_use' | 'other';

/** Mirror of backend `LLMErrorCode` (modelrun/llmCall/base.ts) — per-call
 *  execution codes only. Cross-call verdicts (all-providers-failed) live on
 *  the pipeline layer as PipelineErrorKind, surfaced via SolutionAnalysis
 *  failureKind, not here. */
export type LLMErrorCode =
  | 'max_tokens' | 'safety_block' | 'rate_limit' | 'timeout' | 'invalid_request'
  | 'auth' | 'parse_error' | 'provider_error' | 'unknown';

/** Mirror of backend `RunErrorRecord` (modelrun/llmCall/base.ts). */
export interface RunErrorRecord {
  code: LLMErrorCode;
  stage: 'call' | 'parse';
  provider: string;
  providerCode?: string;
  stopReason?: NormalizedStopReason;
  message: string;
}

/** Mirror of backend `ParallelRoutingTrace` (modelrun/llmCall/base.ts). */
export interface ParallelRoutingTrace {
  parallelSet: string;
  selection: 'default' | 'best-result';
  branches: Array<{
    branch: string;
    rejected: boolean;
    rejectionReason?: 'non-winner' | 'parse-error' | 'call-error';
  }>;
  winnerBranch: string;
  /** True on rejected (non-winner) branches; absent on the winner. */
  rejectedByParallel?: boolean;
}

/** Render a trace's `triggeredBy` — an array of fired checks on new rows, a
 *  single string on pre-migration rows — as a comma-joined label. */
export function triggerLabel(t: QualityCheckName[] | QualityCheckName | undefined): string {
  return Array.isArray(t) ? t.join(', ') : (t ?? '');
}

export type ActualFamilyParams =
  | { kind: 'openai-responses'; isReasoningModel: boolean; reasoningEffort?: 'minimal' | 'low' | 'medium' | 'high'; reasoningSummary?: 'none' | 'auto' | 'concise' | 'detailed'; jsonSchemaName?: string; store: boolean; callerTag?: string }
  | { kind: 'openai-chat-completions'; isNewFamily: boolean; tokenParamName: 'max_tokens' | 'max_completion_tokens' }
  | { kind: 'anthropic-messages'; prefillJson: boolean; imageMimeTypes?: string[]; thinkingEnabled?: boolean; thinkingBudgetTokens?: number }
  | { kind: 'gemini-generate-content'; responseMimeType?: string; strictSchema?: boolean; jsonSchemaName?: string; thinkingBudgetTokens?: number;
      /** As sent, e.g. `MEDIA_RESOLUTION_HIGH`; absent = provider default. */
      mediaResolution?: string;
      /** Images that rode with the call. */
      imageCount?: number }
  | { kind: 'mock' };

export interface ModelOutputTokenUsage {
  prompt: number;
  completion: number;
  total: number;
  /** Cached input tokens — billed at a discount. */
  cached?: number;
  /** Anthropic cache_creation_input_tokens — billed at the cache-write premium. */
  cacheCreation?: number;
  /** Reasoning tokens (subset of completion). OpenAI reasoning models only. */
  reasoning?: number;
  /** Input tokens charged for images (subset of prompt). Gemini only. */
  image?: number;
}

/** One image as an LLM call carried it, minus the bytes. Mirrors the backend
 *  `AttachedImage` (modelrun/modelTypes.ts). `role` distinguishes the student's
 *  own work from question figures and page scans, so the readers below never
 *  have to infer intent from a label string. */
export interface AttachedImage {
  role: 'student' | 'question' | 'context';
  label?: string;
  /** The stored image that was sent, as a URL — on runs since 30 Oct 2026. */
  path?: string;
}

/** What an admin replay was replayed from (model-run-replay-target.md). */
export interface ModelRunReplayOf {
  outputId: string;
  partitionKey: string;
  inputId: string;
  submissionId?: string;
  pipelineName?: string;
  /** The provider the original call was served by. */
  provider: string;
  requestedBy?: string;
  requestedAt: string;
  comment?: string;
}

/** One replay of a run, as the replays list shows it. */
export interface ModelRunReplaySummary {
  id: string;
  provider: string;
  model: string;
  outcome: 'ok' | 'parse_error' | 'call_error';
  error?: string;
  cost: number;
  latencyMs: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens?: number;
  stopReason?: string;
  generatedAt: string;
  replayOf: ModelRunReplayOf;
}

export interface ModelOutput {
  id: string;
  partitionKey: string;
  inputId: string;
  /** `replay`: an admin re-sent another run's call to a different model —
   *  never seen by a student or teacher, never used by an analysis. */
  source: 'production' | 'eval' | 'playground' | 'replay';
  /** Set iff `source === 'replay'`. */
  replayOf?: ModelRunReplayOf;
  userId?: string;
  submissionId?: string;
  datasetId?: string;
  parameters: ModelOutputParameters;
  promptText?: string;
  systemPromptText?: string;
  /** @deprecated Not populated on new rows; visible-answer text is derived
   *  from `rawOutputFull` via `getRawOutputText()`. Kept for legacy rows. */
  rawOutput?: string;
  /** Full serialized LLM output (e.g. JSON-stringified Responses API output[]).
   *  Includes reasoning items. Source of truth for everything derived. */
  rawOutputFull?: string;
  /** Extracted reasoning summary text (Responses API reasoning models). */
  reasoningSummaryText?: string;
  parsedAssessment: Feedback | null;
  /** Parsed extraction payload — populated for `extract_steps` (typed as
   *  ExtractedStepsData) and `extract_visuals` (loose Record-shaped JSON
   *  matching the canonical visual_extraction schema). The runtime payload
   *  for visuals is not actually ExtractedStepsData; the type is kept loose
   *  here and views cast as needed. */
  extractedData?: ExtractedStepsData;
  /** Post-call quality checks (mirror of the backend's). Surfaced in the
   *  parsed-output area to flag suspicious assessments. */
  qualityChecks?: Array<{
    name: string;
    fired: boolean;
    signals?: Record<string, unknown>;
  }>;
  /** Manual evaluation written via `saveManualEvaluation`. */
  manualEvaluation?: ManualEvaluation;
  errors?: string[];
  /** Typed, queryable error records (taxonomy doc). Absent on clean runs and
   *  on rows written before the taxonomy. */
  errorRecords?: RunErrorRecord[];
  /** Why the step was skipped. Set with `status: 'skipped'`. */
  skipReason?: string;
  /** Which step a skipped row stands in for. */
  stepType?: string;
  /** Normalized reason generation ended (e.g. 'max_tokens' for a truncation). */
  stopReason?: NormalizedStopReason;
  /** Raw provider stop value, verbatim (e.g. 'MAX_TOKENS'). */
  providerStopReason?: string;
  /** When the LLM call actually started (real dispatch time), ISO. The reliable
   *  ordering key for the Time column; absent on rows written before it existed,
   *  where the column falls back to `generatedAt − latencyMs`. */
  startedAt?: string;
  latencyMs: number;
  tokenUsage: ModelOutputTokenUsage;
  cost: number;
  // Pipeline & run metadata
  pipelineName?: string;
  stepIndex?: number;
  isPrimary: boolean;
  /** Which attempt of the async job wrote this row (1-indexed). Absent on older
   *  rows and on analyses that did not run as a job. */
  jobAttempt?: number;
  /** The job runner had timed this attempt out before its calls came back. Kept
   *  because the calls happened; never primary, and its scores were not recorded. */
  abandoned?: boolean;
  matchScore?: number;
  matchScoreFactors?: string[];
  /** Every image this LLM call carried, in send order, with the role and label
   *  each was sent under. `label` is emitted by the adapter as a `[label]` text
   *  part immediately before its image — real input the model read, but not
   *  part of the prompt text, so this is the only record of it. Absent on rows
   *  written before image recording. */
  attachedImages?: AttachedImage[];
  generatedAt: string;
  /** Voice realtime lifecycle status; `'skipped'` instead marks a step the
   *  pipeline declined to run (no model called — zero cost/latency/tokens).
   *  `'failover_attempt'` marks a provider call that failed and was replaced by
   *  a backup — a real, billed call that did not serve the result.
   *  See ModelOutputRow.status.
   *  `'running'` / `'orphaned'`: a pipeline call still in flight, or one whose
   *  process died — its row exists from the call's start
   *  (PipelineInfra/model-output-lifecycle-target.md). */
  status?: 'pending' | 'completed' | 'error' | 'skipped' | 'failover_attempt' | 'running' | 'orphaned';
  /** In-flight bookkeeping on a `'running'` row. */
  timeoutMs?: number;
  hop?: number;
  hopStartedAt?: string;
  /** Per-run display label joined from the ModelInput's
   *  `operationContext.runInfo` — e.g. the question range an enrichment batch
   *  covered ("Q1–Q4"). Only present for runs whose job stamped it. */
  runInfo?: string;
}

export interface ModelInputWithOutputs extends ModelInput {
  outputs?: ModelOutput[];
}

export interface ModelDataResponse {
  inputs: ModelInputWithOutputs[];
  total: number;
  page: number;
  pageSize: number;
}

// Output-centric types (one row per output)
export interface ModelOutputRow {
  id: string;
  inputId: string;
  source: string;
  userId?: string;
  userEmail?: string;
  /** Login id, for school roster accounts with no email. */
  userLogin?: string;
  userName?: string;
  /** The submission or job the run was made for. */
  submissionId?: string;
  generatedAt: string;
  parameters: ModelOutputParameters;
  latencyMs: number;
  cost: number;
  isPrimary: boolean;
  subject?: string;
  moduleName?: string;
  questionText?: string;
  questionType?: string;
  pipelineName?: string;
  stepIndex?: number;
  promptName?: string;
  promptType?: string;
  errors?: string[];
  /** Typed, queryable error records (taxonomy doc). Absent on clean runs and
   *  on rows written before the taxonomy. */
  errorRecords?: RunErrorRecord[];
  /** `'skipped'` marks a step the pipeline declined to run (a required prompt
   *  placeholder was unsatisfied). No model was called: cost, latency and
   *  tokens are zero and there is no output. */
  status?: string;
  /** Why the step was skipped. Set with `status: 'skipped'`. */
  skipReason?: string;
  /** Which step a skipped row stands in for — a skip has no prompt to name it. */
  stepType?: string;
  /** Normalized/raw stop reason — lets the Status column flag a silent
   *  truncation (a 200 that stopped mid-output) as failed, not success. */
  stopReason?: string;
  providerStopReason?: string;
}

export interface ModelOutputsResponse {
  outputs: ModelOutputRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** Single message in the reconstructed chat transcript for a textchat
 *  ModelInput. See `getModelData.ts :: reconstructTextChatMessages` —
 *  built at view time from the parent TextChat doc, not snapshotted. */
export interface ReconstructedChatMessage {
  role: 'student' | 'tutor';
  content: string;
  imageUrl?: string;
  imageAction?: string;
  timestamp?: string;
  /** True for the single student message whose turn triggered this LLM call. */
  isCurrentTurn?: boolean;
}

export interface ModelOutputWithInput {
  output: ModelOutput;
  input: ModelInput;
  /** Reconstructed chat transcript visible to the LLM when this turn fired.
   *  Present only for `input.operation === 'textchat'` and when reconstruction
   *  succeeded (chat doc still exists, operationContext intact). */
  chatMessages?: ReconstructedChatMessage[];
}

export interface ModelStats {
  totalInputs: number;
  totalOutputs: number;
  productionInputs: number;
  evalInputs: number;
  avgOutputsPerInput: number;
  recentInputs: number;
  distinctModels: string[];
}

// ============================================
// Eval System Types
// ============================================

export type DatasetCategory = 'golden' | 'development' | 'edge_cases' | 'subject_specific';

export interface EvalDataset {
  id: string;
  readableId: string;
  name: string;
  description: string;
  category: DatasetCategory;
  /** `bench` = a hand-picked assessment eval bench. Absent/`legacy` = the older
   *  platform datasets and the auto-* flag feeds, which the bench UI hides. */
  kind?: 'bench' | 'legacy';
  operation?: string;
  metadata: {
    createdAt: string;
    updatedAt: string;
    version: number;
    inputCount: number;
    labeledCount: number;
  };
  stats?: {
    bySubject: Record<string, number>;
    byDifficulty: Record<string, number>;
    byQuestionType: Record<string, number>;
  };
}

export interface ApproachQuality {
  isComplete: boolean;
  isCorrect: boolean;
  isCalculationMistake: boolean;
  optimalScore: number;  // 0-5
}

export type ApproachCategory = 'optimal' | 'suboptimal' | 'calculation_error' | 'partially_correct' | 'incomplete' | 'wrong_approach' | 'missing';

export interface IdealAssessment {
  // Existing fields
  correctness?: {
    value: LlmValueCorrectness;
    unit?: LlmUnitCorrectness;
  };
  correctLlmAnswer?: string;
  /** Additional acceptable forms of the correct answer (best match wins). */
  correctLlmAnswerAlts?: string[];
  correctLlmSolution?: string;
  approachQuality?: ApproachQuality;

  // New: LLM assessment ground truth
  expectedStudentAnswer?: string;
  /** Additional acceptable forms of the student's answer (best match wins). */
  expectedStudentAnswerAlts?: string[];
  expectedOverallAssessment?: OverallAssessment;
  expectedApproachCategory?: ApproachCategory;
  expectedStepCount?: NumberRange;
  expectedErrorFraction?: number;

  // New: Computed score ground truth (0–10 ranges; single value = min === max).
  expectedFinalScore?: NumberRange;           // required for analysis labeling
  expectedCorrectnessScore?: NumberRange;     // "accuracy" — optional
  expectedApproachScore?: NumberRange;        // optional
  expectedSpeedScore?: NumberRange;           // optional
}

/** Inclusive expected-value range for a numeric eval dimension (single value = min === max). */
export interface NumberRange {
  min: number;
  max: number;
}

export interface EvaluationCriteria {
  requiredFeedbackPoints?: string[];
  prohibitedPoints?: string[];
  idealHints?: string[];
}

// Label data nested within DatasetItem
export interface LabelData {
  labeledOutputId: string;
  evaluationCriteria: EvaluationCriteria;
  confidence: 'high' | 'medium' | 'low';
  notes?: string;
  labeledBy: string;
  labeledAt: string;
}

// DatasetItem - links a ModelInput to a Dataset
export interface DatasetItem {
  id: string;
  datasetId: string;
  modelInputId: string;
  status: 'pending' | 'labeled';
  addedAt: string;
  addedBy?: string;
  labelData?: LabelData;
}

// DatasetItem with its associated ModelInput
export interface DatasetItemWithInput extends DatasetItem {
  input?: ModelInput;
  studentName?: string;
}

export interface DatasetItemListResponse {
  items: DatasetItemWithInput[];
  total: number;
  pendingCount: number;
  labeledCount: number;
}

export const DATASET_CATEGORIES: Record<DatasetCategory, string> = {
  golden: 'High-quality, expert-labeled examples for final evaluation',
  development: 'Larger set for iterative improvement and A/B testing',
  edge_cases: 'Unusual or tricky examples for robustness testing',
  subject_specific: 'Per-subject evaluation sets for specialized tuning'
};

// ============================================
// Eval Run Types
// ============================================

export interface DimensionStats {
  mean: number;
  median: number;
  stdDev: number;
  min: number;
  max: number;
  p25?: number;
  p75?: number;
}

export interface EvalRunParameters {
  promptId: string;
  promptVersion: number;
  provider: string;
  model: string;
  temperature: number;
  maxTokens?: number;
}

export interface EvalRunConfig {
  maxConcurrency: number;
  timeoutMs: number;
  retryCount: number;
  dimensions: string[];
}

export interface EvalRunStatistics {
  total: number;
  successful: number;
  failed: number;
  dimensions: Record<string, DimensionStats>;
  aggregateScore: { mean: number; median: number; stdDev: number };
  totalLatencyMs: number;
  avgLatencyMs: number;
  totalTokens: number;
  totalCost: number;
}

export interface EvalRun {
  id: string;
  readableId: string;
  datasetId: string;
  parameters: EvalRunParameters;
  config: EvalRunConfig;
  statistics: EvalRunStatistics;
  startedAt: string;
  completedAt?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  error?: string;
}

export interface EvalRunListResponse {
  runs: EvalRun[];
  total: number;
}

export interface PerformanceSummary {
  topConfigurations: {
    promptId: string;
    provider: string;
    model: string;
    avgScore: number;
    avgLatency: number;
    avgCost: number;
    runCount: number;
  }[];
  trends: {
    date: string;
    avgScore: number;
    totalRuns: number;
  }[];
  totalRuns: number;
  totalEvaluations: number;
  avgScore: number;
  avgLatency: number;
  totalCost: number;
}

// ── Async-job tuning (Monitoring → Async Jobs → Config) ──────────────────
export interface AsyncJobConfigValues {
  concurrencyCap: number;
  maxAttempts: number;
  backoffMs: number[];
  timeoutMs: number;
  ttlSecondsTerminal: number;
  ttlSecondsActive: number;
}

export interface AsyncJobTimingStats {
  n: number;
  p50: number | null;
  p90: number | null;
  max: number | null;
}

export interface AsyncJobTypeStats {
  type: string;
  /** queued → started: time waiting for a slot. */
  pickup: AsyncJobTimingStats;
  /** started → completed: time doing the work. */
  exec: AsyncJobTimingStats;
  /** In flight right now — point-in-time, unlike the percentiles beside it. */
  runningNow: number;
}

export interface AsyncJobTypeConfig {
  type: string;
  /** What the dispatcher uses right now. */
  effective: AsyncJobConfigValues;
  /** Compiled-in fallback, for the reset affordance. */
  handlerDefault: AsyncJobConfigValues;
  overridden: boolean;
}
